/**
 * State of the patient's «جاباز» queue seats, a mirror of the queue service (dabbeh, ../queue.ts), and of
 * the subscription as Plus answered it on this page. Seats are kept in localStorage so a reload shows them
 * at once; the subscription is memory only (see `partialize`): Plus is asked again on every screen.
 * Without the service (JABAZ_API_URL empty) the actions below run the whole flow on this device (prototype).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { JABAZ_HOLD_MINUTES, JABAZ_PRICE_TOMAN, JABAZ_QUEUE_AHEAD, JABAZ_SUBSCRIPTION_DAYS } from '../constants';
import type { JabazDoctor, JabazMembership, JabazSubscription } from '../types';

const DAY = 24 * 3600_000;
const iso = (offsetMs = 0) => new Date(Date.now() + offsetMs).toISOString();
/** Eight digits, the same as the queue service's tracking codes. */
const code = () => String(Math.floor(10_000_000 + Math.random() * 90_000_000));

/** Visit time of a released appointment: tomorrow 10:20 (sample until the service provides it). */
const sampleVisitAt = () => {
  const at = new Date();
  at.setDate(at.getDate() + 1);
  at.setHours(10, 20, 0, 0);
  return at.toISOString();
};

export const isSubscriptionActive = (sub: JabazSubscription | null, now = Date.now()) =>
  !!sub && new Date(sub.endsAt).getTime() > now;

interface JabazStore {
  /** Doctor picked in the sheet, waiting for checkout. */
  draft: JabazDoctor | null;
  subscription: JabazSubscription | null;
  /** What Plus answered on this page: an active subscription, none, or not asked / not answered yet. */
  plusAnswer: 'active' | 'none' | null;
  /** Queue seats by doctor slug. */
  memberships: Record<string, JabazMembership>;
  /** People already waiting for a doctor before the patient joins (test tool can set 0 = empty queue). */
  queueAhead: number;
  setDraft: (doctor: JabazDoctor) => void;
  /** Buys (or renews) the subscription and seats the patient in the draft doctor's queue. */
  purchase: (paid?: JabazSubscription | null) => JabazMembership | null;
  /** With an active subscription: seats the patient in `doctor`'s queue, nothing to pay. */
  joinWithSubscription: (doctor: JabazDoctor) => JabazMembership | null;
  leave: (slug: string) => void;
  /** «انصراف از این نوبت» or a missed hold: the appointment passes on, the patient goes to the end of the line. */
  passOffer: (slug: string) => void;
  /** The patient confirmed the held appointment (booking continues in the regular factor). */
  confirmOffer: (slug: string) => void;
  /** Test tool: put a seat in one of the states the service will send. */
  setState: (slug: string, state: 'waiting' | 'offered' | 'missed' | 'expired') => void;
  setQueueAhead: (count: number) => void;
  /** The queue service's (dabbeh) seats replace this device's (rollout doctors only). */
  syncSeats: (seats: JabazMembership[]) => void;
  /** The real subscription from Plus (same on every device). */
  setSubscription: (subscription: JabazSubscription) => void;
  /** Plus says there is no active subscription: one this device still holds as active is marked ended. */
  endSubscription: () => void;
  reset: () => void;
}

const newMembership = (doctor: JabazDoctor, ahead: number, viaSubscription: boolean): JabazMembership => ({
  doctor,
  joinedAt: iso(),
  position: ahead + 1,
  trackingCode: code(),
  status: 'waiting',
  viaSubscription,
});

export const useJabazStore = create<JabazStore>()(
  persist(
    (set, get) => ({
      draft: null,
      subscription: null,
      plusAnswer: null,
      memberships: {},
      queueAhead: JABAZ_QUEUE_AHEAD,
      setDraft: doctor => set({ draft: doctor }),
      purchase: paid => {
        const doctor = get().draft;
        if (!doctor) return null;
        const subscription: JabazSubscription = paid ?? {
          startedAt: iso(),
          endsAt: iso(JABAZ_SUBSCRIPTION_DAYS * DAY),
          trackingCode: code(),
          pricePaid: JABAZ_PRICE_TOMAN,
        };
        const existing = get().memberships[doctor.slug];
        const membership = existing
          ? { ...existing, doctor, status: 'waiting' as const, offer: undefined, position: get().queueAhead + 1, viaSubscription: false }
          : newMembership(doctor, get().queueAhead, false);
        set({ subscription, memberships: { ...get().memberships, [doctor.slug]: membership } });
        return membership;
      },
      joinWithSubscription: doctor => {
        if (!isSubscriptionActive(get().subscription)) return null;
        const membership = get().memberships[doctor.slug] ?? newMembership(doctor, get().queueAhead, true);
        set({ draft: doctor, memberships: { ...get().memberships, [doctor.slug]: membership } });
        return membership;
      },
      leave: slug => {
        const { [slug]: _left, ...rest } = get().memberships;
        set({ memberships: rest });
      },
      passOffer: slug => {
        const seat = get().memberships[slug];
        if (!seat) return;
        set({
          memberships: {
            ...get().memberships,
            [slug]: { ...seat, status: 'missed', offer: undefined, position: get().queueAhead + 1 },
          },
        });
      },
      confirmOffer: slug => get().leave(slug),
      setState: (slug, state) => {
        const seat = get().memberships[slug];
        if (!seat) return;
        const sub = get().subscription;
        if (state === 'expired') {
          set({ subscription: sub ? { ...sub, endsAt: iso(-60_000) } : sub, memberships: { ...get().memberships, [slug]: { ...seat, status: 'waiting', offer: undefined } } });
          return;
        }
        // any other state needs a live subscription
        const live = sub && isSubscriptionActive(sub) ? sub : sub ? { ...sub, endsAt: iso(JABAZ_SUBSCRIPTION_DAYS * DAY) } : sub;
        const next: JabazMembership =
          state === 'offered'
            ? { ...seat, status: 'offered', position: 1, offer: { visitAt: sampleVisitAt(), holdExpiresAt: iso(JABAZ_HOLD_MINUTES * 60_000) } }
            : state === 'missed'
              ? { ...seat, status: 'missed', offer: undefined, position: get().queueAhead + 1 }
              : { ...seat, status: 'waiting', offer: undefined, position: get().queueAhead + 1 };
        set({ subscription: live, memberships: { ...get().memberships, [slug]: next } });
      },
      setQueueAhead: count => set({ queueAhead: Math.max(0, count) }),
      setSubscription: subscription => set({ subscription, plusAnswer: isSubscriptionActive(subscription) ? 'active' : 'none' }),
      endSubscription: () => {
        const sub = get().subscription;
        // one seen active on this page keeps its dates («پایان اعتبار: …»), marked ended
        set({ plusAnswer: 'none', subscription: sub && isSubscriptionActive(sub) ? { ...sub, endsAt: iso(-60_000) } : sub });
      },
      syncSeats: seats => set({ memberships: Object.fromEntries(seats.map(seat => [seat.doctor.slug, seat])) }),
      reset: () => set({ draft: null, subscription: null, plusAnswer: null, memberships: {}, queueAhead: JABAZ_QUEUE_AHEAD }),
    }),
    {
      name: 'jabaz-state',
      version: 2,
      // the subscription is never kept on the device: Plus alone says whether it is active, on every page
      // (a copy left here once said «paid» or «ended» when Plus said otherwise)
      partialize: state => ({ draft: state.draft, memberships: state.memberships, queueAhead: state.queueAhead }),
      migrate: persisted => {
        const { subscription: _dropped, ...rest } = (persisted ?? {}) as Partial<JabazStore>;
        return rest as JabazStore;
      },
    },
  ),
);
